'use client'

import { useEffect, useMemo, useRef } from 'react'

import { styled } from 'styled-components'

import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'
import { FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { Colors } from '@b-stock/fe-core/design-system'

import { recentSearchQuery } from '@/queries/recentSearchQuery'
import suggestedSearchQuery from '@/queries/suggestedSearchQuery'

import {
  SearchDropdownWrapper,
  SearchDropdownContainer,
  SearchDropdownItem,
} from './ui'

interface SearchAutosuggestionProps {
  isOpen: boolean
  onSelectSuggestion: (
    suggestion: string,
    source?: 'recent' | 'trending'
  ) => void
  onClose: () => void
  selectedIndex: number
  inputRef?: React.RefObject<HTMLInputElement | null>
  onSuggestionsChange?: (suggestions: string[]) => void
}

const SectionHeader = styled.div`
  padding: 0.875rem 1.5rem 0.4375rem 1.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: ${Colors.Semantic.Neutral.black};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

const AutosuggestionItem = styled(SearchDropdownItem)`
  padding: 0.4375rem 1.5rem 0.4375rem 2rem;
`

export default function SearchAutosuggestion({
  isOpen,
  onSelectSuggestion,
  onClose,
  selectedIndex,
  inputRef,
  onSuggestionsChange,
}: SearchAutosuggestionProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const selectedItemRef = useRef<HTMLButtonElement>(null)

  // Get user identity from decoded token
  const decodedToken = useDecodedAccessToken()
  const accountId = decodedToken?.accountId
  const personId = decodedToken?.personId

  // Fetch recent searches from API
  const { data: recentSearchData } = recentSearchQuery.useWithOpts(
    [{ limit: 5, accountId, personId }],
    { enabled: isOpen }
  )

  // Extract recent search names from API response
  const recentSearches = useMemo(
    () =>
      recentSearchData?.recentSearches.map((s: { name: string }) => s.name) ||
      [],
    [recentSearchData]
  )

  // Fetch suggested searches from API
  const { data: suggestedSearchData } = suggestedSearchQuery.useWithOpts(
    [{ limit: 5 }],
    { enabled: isOpen }
  )

  // Extract suggested search queries from API response
  const suggestedSearches = useMemo(
    () => suggestedSearchData?.suggestedSearches.map((s) => s.query) || [],
    [suggestedSearchData]
  )

  // Combined list of all suggestions for parent to use in keyboard navigation
  const allSuggestions = useMemo(
    () => [...recentSearches, ...suggestedSearches],
    [recentSearches, suggestedSearches]
  )

  // Notify parent when suggestions change
  useEffect(() => {
    if (onSuggestionsChange && isOpen) {
      onSuggestionsChange(allSuggestions)
    }
  }, [allSuggestions, isOpen, onSuggestionsChange])

  // Scroll selected item into view when keyboard navigation changes
  useEffect(() => {
    if (selectedItemRef.current && selectedIndex >= 0) {
      selectedItemRef.current.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      })
    }
  }, [selectedIndex])

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const isClickOutsideContainer =
        containerRef.current && !containerRef.current.contains(target)
      const isClickOnInput =
        inputRef?.current && inputRef.current.contains(target)

      // Close only if clicking outside both the container and the input field
      if (isClickOutsideContainer && !isClickOnInput) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose, inputRef])

  if (!isOpen) return null

  return (
    <SearchDropdownWrapper $isOpen={isOpen}>
      <SearchDropdownContainer
        ref={containerRef}
        role="listbox"
        aria-label="Search suggestions"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {recentSearches.length > 0 && (
          <>
            <SectionHeader>
              <FormattedMessage id="Search.autosuggestion.recentSearches" />
            </SectionHeader>
            {recentSearches.map((search: string, index: number) => (
              <AutosuggestionItem
                key={`recent-${search}`}
                ref={index === selectedIndex ? selectedItemRef : null}
                $isSelected={index === selectedIndex}
                onClick={() => onSelectSuggestion(search, 'recent')}
                onMouseDown={(e) => e.preventDefault()}
                role="option"
                aria-selected={index === selectedIndex}
                data-testid={`autosuggestion-recent-${index}`}
              >
                {search}
              </AutosuggestionItem>
            ))}
          </>
        )}

        {suggestedSearches.length > 0 && (
          <>
            <SectionHeader>
              <FormattedMessage id="Search.autosuggestion.trendingSearches" />
            </SectionHeader>
            {suggestedSearches.map((search, index) => {
              const itemIndex = recentSearches.length + index
              return (
                <AutosuggestionItem
                  key={`trending-${search}`}
                  ref={itemIndex === selectedIndex ? selectedItemRef : null}
                  $isSelected={itemIndex === selectedIndex}
                  onClick={() => onSelectSuggestion(search, 'trending')}
                  onMouseDown={(e) => e.preventDefault()}
                  role="option"
                  aria-selected={itemIndex === selectedIndex}
                  data-testid={`autosuggestion-trending-${index}`}
                >
                  {search}
                </AutosuggestionItem>
              )
            })}
          </>
        )}
      </SearchDropdownContainer>
    </SearchDropdownWrapper>
  )
}
